import moment from "moment";
import { isObject, isArray } from "@utils/Browser";
import { getRichTextEntityLinks } from "@contentful/rich-text-links";
import { base64decodeUrl } from "@utils/Browser";
import { BLOCKS } from "@contentful/rich-text-types";

const PROMO_INJECT_POSITIONS = [1, 4, 6];

class ContentStore {
	content;
	service;
	relatedContent;
	popularCities = [];
	relatedServices = [];
	refs = [];

	constructor(
		rootStore,
		{ content, service, relatedContent, popular_cities, related_services }
	) {
		this.rootStore = rootStore;
		this.content = JSON.parse(base64decodeUrl(content) || "{}");
		this.relatedContent = relatedContent;
		this.service = service;
		this.popularCities = popular_cities;
		this.relatedServices = related_services;
	}

	injectPromos(contentBody) {
		const h2Positions = contentBody
			.map((node, index) => node.nodeType === "heading-2" && index)
			.filter((f) => f !== false);
		const entry = {
			nodeType: BLOCKS.EMBEDDED_ENTRY,
			data: {
				__typename: "InjectedPromo",
				header: `Get free estimates from ${
					this.service?.phrase?.toLowerCase() ?? ""
				} near you.`,
			},
			content: [],
		};

		PROMO_INJECT_POSITIONS.forEach((placement) => {
			if (h2Positions[placement] !== undefined) {
				contentBody.splice(h2Positions[placement], 0, entry);
			}
		});
	}

	get contentBody() {
		this.injectPromos(this.content.body.json.content);
		return this.content.body;
	}

	get authors() {
		return this.content.authorsCollection.items.filter(
			(item) => item?.type.toLowerCase() === "writer"
		);
	}

	get editors() {
		return this.content.authorsCollection.items.filter(
			(item) => item?.type.toLowerCase() === "editor"
		);
	}

	get reviewers() {
		return this.content.authorsCollection.items.filter(
			(item) => item?.type.toLowerCase() === "reviewer"
		);
	}

	get publishedAt() {
		return {
			raw: this.content.sys.publishedAt,
			pretty: moment(this.content.sys.publishedAt).format("LL"),
			standard: moment(this.content.sys.publishedAt).format("YYYY-MM-DD"),
		};
	}

	get firstPublishedAt() {
		return {
			raw: this.content.sys.firstPublishedAt,
			pretty: moment(this.content.sys.firstPublishedAt).format("LL"),
			standard: moment(this.content.sys.firstPublishedAt).format("YYYY-MM-DD"),
		};
	}

	get hasTableOfContents() {
		return !!this.content.rightColumn?.tableOfContents;
	}

	get hasAltTableOfContents() {
		return !!this.content.rightColumn?.tocAlt;
	}

	get sources() {
		return this.content.sources2?.json;
	}

	get relatedArticles() {
		return this.relatedContent.map(({ folder, slug, thumbnail, ...other }) => ({
			...other,
			url: `${folder}${slug}`,
			slug,
			thumbnail: thumbnail
				? { ...thumbnail, cdnUrl: this.__generateUrl(thumbnail.fileName) }
				: thumbnail,
		}));
	}

	get bodyAssets() {
		const { Asset: bodyAsset } = getRichTextEntityLinks(this.content.body.json);
		const bodyAssetIds = bodyAsset.map((asset) => asset.id);
		return this.assets.filter((asset) => bodyAssetIds.includes(asset.sys.id));
	}

	get assets() {
		const { Asset: bodyAsset } = getRichTextEntityLinks(this.content.body.json);
		const { Asset: summaryAsset } = getRichTextEntityLinks(
			this.content.summary?.text?.json
		);
		const { Asset: heroSnippetAsset } = getRichTextEntityLinks(
			this.content.hero?.snippet2?.json
		);
		return [...bodyAsset, ...summaryAsset, ...heroSnippetAsset]
			.filter(Boolean)
			.map(({ id }) =>
				this.findLinkedEntity(
					{ data: { target: { sys: { id } } } },
					this.content.body.links?.assets
				)
			)
			.filter(Boolean)
			.map((asset) => ({
				...asset,
				url: this.__generateUrl(asset.fileName),
			}));
	}

	get thumbnailUrl() {
		const { fileName } = this.content.thumbnail || {};
		if (!fileName) return;
		return this.__generateUrl(fileName);
	}

	__generateUrl(fileName) {
		return `https://static.${this.content.site}.com/assets/images/content/${fileName}`;
	}

	findLinkedEntity(embeddedNodeBlock, links = {}) {
		if (!embeddedNodeBlock) {
			throw "must pass in a node";
		}
		const targetId = embeddedNodeBlock.data.target.sys.id;
		let response;

		for (const entryType in links) {
			if (entryType === "__typename") continue;
			if (links.hasOwnProperty(entryType) === false) continue;
			const entries = links[entryType];
			const targetEntry = entries
				.filter(Boolean)
				.find((entry) => entry.sys?.id === targetId);
			if (targetEntry) {
				response = targetEntry;
				break;
			}
		}
		return response;
	}

	findHashTagFromNode(node) {
		const recursivelyGetValuesOfNode = (input, response = []) => {
			if (isObject(input)) {
				const values = Object.values(input);
				for (const value of values) {
					if (isObject(value)) recursivelyGetValuesOfNode(value, response);
					else if (isArray(value)) recursivelyGetValuesOfNode(value, response);
					else response.push(value);
				}
			} else if (isArray(input)) {
				for (const item of input) {
					recursivelyGetValuesOfNode(item, response);
				}
			}
			return response;
		};
		const items = recursivelyGetValuesOfNode(node);
		return items.find((item) => item.indexOf("#") === 0);
	}

	findAssetById(assetId) {
		return this.assets.find((asset) => asset.sys.id === assetId);
	}

	refsReset() {
		this.refs = [];
	}
}

export default ContentStore;
